import { Injectable } from '@angular/core';
import { ConfigService } from '@finder/core/config.service';
import { fromEvent, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { MESSAGE, OPEN } from './websockets.constants';
import { CONFIG_CONSTANTS } from '@finder/shared/constants/app.constants';
import getApiBase from '@finder/shared/utils/string/getApiBase';
import { DebugLogService } from '../debug-log/debug-log.service';
import { OperatingStatusService } from '@finder/shared/components/operating-status/operating-status.service';
import { LocaleService } from '../locale-service/locale-service.service';
import { OperatingStatus } from '@finder/shared/components/operating-status/operating-status.interface';

@Injectable({
    providedIn: 'root'
})
export class WebSocketsService {
    private webSocket;
    private baseUrl = '';

    private destroy$ = new Subject<void>();

    constructor(
        private config: ConfigService,
        private debugLogService: DebugLogService,
        private operatingStatusService: OperatingStatusService,
        private localeService: LocaleService,
    ) {
        const base = this.config.getValue(CONFIG_CONSTANTS.webSocketUrlKey);
        const version = this.config.getValue(CONFIG_CONSTANTS.defaultVersionKey);
        const siteId = this.config.getValue(CONFIG_CONSTANTS.siteIdKey);

        this.baseUrl = `${getApiBase(base[siteId], version)}/explorer-service/dash-public/${siteId}`;
    }

    cleanUp() {
        // We don't complete the subject because we reuse it
        this.destroy$.next();

        // Close the webSocket
        if (this.webSocket) {
            this.webSocket.close();
        }
        /**
         * https://dev.to/bo/when-a-service-got-destroyed-in-angular-1bd5
         * Component level services are destroyed when the page changes.
         * Root level services stick around
         */
    }

    detailConnection(facilityId, updatePermissions) {
        // Create the websocket server
        const url = `${this.baseUrl}/detail`;

        this.onConnect(url, {
            facilityId,
            updatePermissions,
            pageType: 'detail',
            siteId: this.config.getValue(CONFIG_CONSTANTS.siteIdKey),
            locale: this.localeService.getNormalizedLocale()
        }, (statusUpdate: OperatingStatus) => {
            this.operatingStatusService.sendUpdate(statusUpdate);
        });
    }

    listConnection(updatePermissions) {
        // Create the websocket server
        const url = `${this.baseUrl}/list`;

        this.onConnect(url, {
            updatePermissions,
            pageType: 'list',
            siteId: this.config.getValue(CONFIG_CONSTANTS.siteIdKey),
            locale: this.localeService.getNormalizedLocale()
        }, (statusUpdates: Array<OperatingStatus>) => {
            // Loop through the responses and send each update out
            statusUpdates.forEach((statusUpdate: OperatingStatus) => {
                this.operatingStatusService.sendUpdate(statusUpdate);
            });
        });
    }

    searchConnection(facilityIds, updatePermissions) {
        // Create the websocket server
        const url = `${this.baseUrl}/search`;

        this.onConnect(url, {
            facilityIds,
            updatePermissions,
            pageType: 'search',
            siteId: this.config.getValue(CONFIG_CONSTANTS.siteIdKey),
            locale: this.localeService.getNormalizedLocale()
        }, (statusUpdates: Array<OperatingStatus>) => {
            // Loop through the responses and send each update out
            statusUpdates.forEach((statusUpdate: OperatingStatus) => {
                this.operatingStatusService.sendUpdate(statusUpdate);
            });
        });
    }

    /* istanbul ignore next */
    getWebSocket(url) {
        return new WebSocket(url);
    }

    onConnect(url, sendOnConnect, onResponse) {
        // Create the websocket server
        this.webSocket = this.getWebSocket(url);

        // Make the connection to the server and send the initial payload
        fromEvent(this.webSocket, OPEN)
            .pipe(
                take(1)
            ).subscribe(() => {
                // Once the connection is open send it the wait time and status
                this.webSocket.send(JSON.stringify(sendOnConnect));
            });

        // Listen for a message from the server and parse the response
        fromEvent(this.webSocket, MESSAGE)
            .pipe(
                takeUntil(this.destroy$),
                switchMap((response: MessageEvent) => of(JSON.parse(response.data)))
            ).subscribe((response: any) => {
                // If the response has a message just log it and stop
                if (response.message) {
                    this.debugLogService.log(response.message);

                    return;
                }

                onResponse(response);
            });
    }
}
